/**
 * Public landing page — storytelling style modeled after vivaesg.it.
 * Visible when not authenticated; logged-in users are redirected here from
 * the dashboard footer link as well.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowDown, ArrowRight, Leaf, Users, ShieldCheck, BarChart3,
  Sparkles, FileText, Layers, BookOpen, LogIn,
} from "lucide-react";

function Section({ children, className = "", id }) {
  return (
    <section id={id} className={`px-6 sm:px-12 lg:px-24 py-24 ${className}`}>
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

function Quote({ text, author }) {
  return (
    <figure className="my-12 text-center">
      <blockquote className="font-heading italic text-xl sm:text-2xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
        “{text}”
      </blockquote>
      <figcaption className="mt-4 text-sm uppercase tracking-[0.2em] text-slate-400">
        — {author}
      </figcaption>
    </figure>
  );
}

function FeatureCard({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-3xl bg-white border border-slate-100 p-7 shadow-soft hover:shadow-lg transition group">
      <div className="w-12 h-12 rounded-2xl bg-viva-100 flex items-center justify-center text-viva-700 mb-4 group-hover:scale-110 transition-transform">
        <Icon className="w-6 h-6" strokeWidth={1.75} />
      </div>
      <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Smooth scroll for anchor links
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => { document.documentElement.style.scrollBehavior = "auto"; };
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="landing-page">
      {/* ----------- TOP NAV ----------- */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-lg bg-white/85 border-b border-slate-200/60">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 lg:px-24 h-16 flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-2.5">
            <img src="/viva_logo.png" alt="VIVA ESG" className="w-10 h-10 object-contain" />
            <div className="flex flex-col leading-none">
              <span className="font-heading font-bold text-viva-700 text-lg">VIVA ESG</span>
              <span className="text-[9px] uppercase tracking-[0.2em] text-slate-400 font-semibold mt-0.5">Rating Platform</span>
            </div>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#missione" className="hover:text-viva-700">Missione</a>
            <a href="#piattaforma" className="hover:text-viva-700">Piattaforma</a>
            <a href="#contatti" className="hover:text-viva-700">Contatti</a>
          </nav>
          <Button
            onClick={() => navigate(user ? "/app" : "/login")}
            data-testid="landing-login-cta"
            className="bg-viva-600 hover:bg-viva-700 text-white rounded-full px-5"
          >
            {user ? "Vai alla piattaforma" : "Accedi"} <LogIn className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </header>

      {/* ----------- HERO ----------- */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center px-6 sm:px-12 lg:px-24 pt-24 pb-20 bg-white">
        <div className="relative max-w-5xl mx-auto text-center">
          <img
            src="/viva_logo.png"
            alt="VIVA ESG"
            className="w-44 h-44 sm:w-56 sm:h-56 mx-auto object-contain drop-shadow-lg mb-10 animate-viva-breathe"
          />
          <Quote
            text="Credo che avere la terra e non rovinarla sia la più bella forma d'arte che si possa desiderare."
            author="Andy Warhol, 1975"
          />
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 leading-[1.05] mt-12">
            Noi siamo <span className="text-viva-700">custodi</span>,<br />
            non protagonisti.
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            VIVA ESG è un'identità collettiva, una coscienza ambientale che prende vita
            in ogni progetto, in ogni cliente, in ogni azione.
          </p>
          <div className="mt-12 flex items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate(user ? "/app" : "/login")}
              data-testid="landing-hero-cta"
              className="bg-viva-600 hover:bg-viva-700 text-white rounded-full px-8 py-6 text-base"
            >
              {user ? "Vai alla piattaforma" : "Accedi alla piattaforma"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a
              href="#missione"
              className="hidden sm:inline-flex items-center gap-1 text-sm text-slate-500 hover:text-viva-700 font-medium"
            >
              Scopri di più <ArrowDown className="w-3.5 h-3.5" />
            </a>
          </div>
          <a href="#missione" className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:block animate-bounce">
            <ArrowDown className="w-5 h-5 text-viva-600" />
          </a>
        </div>
      </section>

      {/* ----------- IDENTITY ----------- */}
      <Section className="bg-white">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-viva-700 mb-3">La nostra identità</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
              Siamo i custodi del nostro pianeta, dell'ambiente che ci circonda e dell'ecosistema in cui viviamo.
            </h2>
          </div>
          <div className="text-lg text-slate-600 leading-relaxed space-y-5">
            <p>
              Non ti diremo che sei sostenibile.
            </p>
            <p className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 leading-tight">
              Ti aiuteremo a <span className="text-viva-700">dimostrarlo</span>.
            </p>
            <p className="text-base">
              Con metriche misurabili, processi rigorosi e una piattaforma di rating ESG
              costruita sui modelli ufficiali italiani — non sui claim di marketing.
            </p>
          </div>
        </div>
      </Section>

      {/* ----------- NUMERI / VALORI ----------- */}
      <Section className="bg-white">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-viva-700 mb-3">I numeri parlano</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 leading-tight max-w-3xl mx-auto">
            Ogni numero ha un volto.<br />Ogni dato ha una responsabilità.
          </h2>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed italic">
            L'energia che salvi · La CO₂ che eviti · La terra che rigeneri
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Leaf, label: "Environmental", color: "text-emerald-700", bg: "bg-emerald-100", desc: "Cambiamento climatico, risorse idriche, biodiversità, economia circolare, energia, mobilità sostenibile" },
            { icon: Users, label: "Social", color: "text-violet-700", bg: "bg-violet-100", desc: "Forza lavoro, diversità e inclusione, welfare, comunità, consumatori, diritti umani lungo la filiera" },
            { icon: ShieldCheck, label: "Governance", color: "text-indigo-700", bg: "bg-indigo-100", desc: "Condotta aziendale, organi di governo, integrazione strategica, risk management, compliance, supply chain" },
          ].map((p) => (
            <div key={p.label} className="rounded-3xl bg-white p-8 border border-slate-100 shadow-soft">
              <div className={`w-14 h-14 rounded-2xl ${p.bg} ${p.color} flex items-center justify-center mb-5`}>
                <p.icon className="w-7 h-7" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">{p.label}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ----------- MISSIONE & VISIONE ----------- */}
      <Section id="missione" className="bg-white">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="rounded-3xl bg-gradient-to-br from-viva-600 to-viva-700 text-white p-10 sm:p-14 relative overflow-hidden">
            <div className="absolute -bottom-12 -right-12 opacity-10">
              <Leaf className="w-64 h-64" strokeWidth={0.8} />
            </div>
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.25em] font-bold opacity-70 mb-4">La nostra missione</div>
              <h3 className="font-heading text-3xl sm:text-4xl font-bold leading-tight">
                Aiutare le aziende a costruire un futuro in cui l'efficienza si misura e l'impatto si riduce.
              </h3>
              <p className="mt-6 text-base opacity-90 leading-relaxed">
                Dotare ogni impresa di una governance solida e dinamica, capace di rendere
                tracciabile, misurabile e migliorabile il proprio impatto ESG.
              </p>
            </div>
          </div>
          <div className="rounded-3xl bg-white p-10 sm:p-14 border border-slate-100 shadow-soft">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-viva-700 mb-4">La nostra visione</div>
            <h3 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
              Un'economia che sia una risposta <span className="italic">attiva, etica, audace</span> alla crisi climatica.
            </h3>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              Una visione radicale: trasformare la sostenibilità da slogan a prassi quotidiana,
              da costo a vantaggio competitivo, da obbligo a opportunità.
            </p>
          </div>
        </div>
      </Section>

      {/* ----------- PIATTAFORMA ----------- */}
      <Section id="piattaforma" className="bg-white">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-viva-700 mb-3">La piattaforma</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
            VIVA ESG Rating Platform
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Calcolo del rating ESG su 126 indicatori, allineato ai modelli ufficiali italiani.
            Tutto in un'unica piattaforma cloud.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          <FeatureCard
            icon={BarChart3}
            title="Rating ESG V1-V7"
            desc="126 indicatori, profili BASE/STANDARD/ESTESO, benchmark per 10 cluster settoriali, score in tempo reale."
          />
          <FeatureCard
            icon={Layers}
            title="Doppia Materialità"
            desc="Matrice di materialità ESRS/CSRD con 21 topic, impatto + finanziaria, motivazioni per ogni voce."
          />
          <FeatureCard
            icon={Sparkles}
            title="Piano d'Azione AI"
            desc="Raccomandazioni operative generate da Claude Sonnet 4.6 per ogni topic con score basso. Italiano, settoriale, misurabile."
          />
          <FeatureCard
            icon={FileText}
            title="Report & Certificato"
            desc="PDF di 17 pagine con sintesi esecutiva, metodologia, sezioni narrative, certificato landscape pronto da firmare."
          />
          <FeatureCard
            icon={BookOpen}
            title="VSME / ESRS Disclosure"
            desc="Modulo add-on per la rendicontazione di sostenibilità a norma CSRD/VSME su 40+ punti dichiarativi."
          />
          <FeatureCard
            icon={ShieldCheck}
            title="Audit Log & Tracciabilità"
            desc="Cronologia di ogni modifica a pesi e benchmark con diff puntuale, utente, timestamp ed export CSV."
          />
          <FeatureCard
            icon={Users}
            title="Multi-ruolo & RBAC"
            desc="Admin, valuatore, cliente, revisore — ogni profilo vede solo quello che gli serve, con accessi controllati."
          />
          <FeatureCard
            icon={Leaf}
            title="Evidenze documentali"
            desc="Upload di evidenze per ogni indicatore, pannello laterale aggiornato in tempo reale, audit-ready."
          />
        </div>
      </Section>

      {/* ----------- CTA ----------- */}
      <Section className="bg-viva-700 text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold leading-tight">
            VIVA ESG aiuta le imprese a dotarsi di una governance solida e dinamica.
          </h2>
          <p className="mt-6 text-lg opacity-90 leading-relaxed">
            Trasforma la sostenibilità della tua azienda in metriche oggettive,
            certificabili e migliorabili. Inizia oggi.
          </p>
          <Button
            size="lg"
            onClick={() => navigate(user ? "/app" : "/login")}
            data-testid="landing-bottom-cta"
            className="mt-10 bg-white text-viva-700 hover:bg-viva-50 rounded-full px-8 py-6 text-base font-semibold"
          >
            {user ? "Vai alla piattaforma" : "Accedi alla piattaforma"} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </Section>

      {/* ----------- FOOTER ----------- */}
      <footer id="contatti" className="bg-slate-900 text-slate-300 px-6 sm:px-12 lg:px-24 py-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src="/viva_logo.png" alt="VIVA ESG" className="w-12 h-12 object-contain" />
              <div className="font-heading font-bold text-white text-xl">VIVA ESG</div>
            </div>
            <p className="text-sm leading-relaxed opacity-70">
              Custodi dell'ambiente, della governance e del futuro delle imprese italiane.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-viva-300 mb-4">Sede</div>
            <p className="text-sm leading-relaxed">
              VIVA ESG S.r.l.<br />
              Via Eleonora Duse, 5H<br />
              00197 Roma<br />
              <span className="opacity-70">C.F. / P.IVA 17815411008</span>
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-viva-300 mb-4">Contatti</div>
            <ul className="text-sm space-y-1.5">
              <li><a href="tel:+390683390629" className="hover:text-viva-300">Tel +39 06 83390629</a></li>
              <li><a href="tel:+393793021655" className="hover:text-viva-300">Mob +39 379 3021655</a></li>
              <li><a href="https://www.vivaesg.it" target="_blank" rel="noopener noreferrer" className="hover:text-viva-300 font-medium">www.vivaesg.it</a></li>
              <li className="mt-3"><a href="mailto:tecnico@vivaesg.it" className="hover:text-viva-300">tecnico@vivaesg.it</a></li>
              <li><a href="mailto:amministrazione@vivaesg.it" className="hover:text-viva-300">amministrazione@vivaesg.it</a></li>
              <li><a href="mailto:commerciale@vivaesg.eu" className="hover:text-viva-300">commerciale@vivaesg.eu</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-12 pt-6 border-t border-slate-700 text-xs text-slate-500 flex flex-col sm:flex-row justify-between items-center gap-2">
          <span>© {new Date().getFullYear()} VIVA ESG S.r.l. — Tutti i diritti riservati.</span>
          <span className="italic">Non ti diremo che sei sostenibile. Ti aiuteremo a dimostrarlo.</span>
        </div>
      </footer>
    </div>
  );
}
